import { Sparkles, Image as ImageIcon, ImagePlus, Brush, Type, Check, Download, X, Images, ChevronLeft, ChevronRight } from 'lucide-react';
import type { GeneratedImage } from './types';
import { LazyImage } from '../../components/LazyImage';

interface CanvasProps {
  images: GeneratedImage[];
  isLoading: boolean;
  onImageClick?: (image: GeneratedImage) => void;
  onUseAsReference?: (image: GeneratedImage) => void;
  onUseAsInpaint?: (image: GeneratedImage) => void;
  onFillPrompt?: (image: GeneratedImage) => void;
  onFillReference?: (image: GeneratedImage) => void;
  onDownload?: (image: GeneratedImage) => void;
  filledPromptImageId?: string | null;
  filledReferenceImageId?: string | null;
  /** modelId → 模型展示名映射，供大图下方标注模型名 */
  modelNameMap?: Map<string, string>;
  /** 单图模式下「上一张 / 下一张」切换：常用于在画布上浏览最近作品列表 */
  onPrevImage?: () => void;
  onNextImage?: () => void;
  /** 当前位置信息（用于显示「3 / 12」徽标）；未提供时不显示徽标 */
  navIndex?: number;
  navTotal?: number;
}

export function Canvas({
  images,
  isLoading,
  onImageClick,
  onUseAsReference,
  onUseAsInpaint,
  onFillPrompt,
  onFillReference,
  onDownload,
  filledPromptImageId,
  filledReferenceImageId,
  modelNameMap,
  onPrevImage,
  onNextImage,
  navIndex,
  navTotal,
}: CanvasProps) {
  if (isLoading && images.length === 0) {
    return (
      <div className="card h-[400px] flex flex-col items-center justify-center">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-light-border border-t-accent-blue rounded-full animate-spin" />
          <div className="absolute inset-0 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-accent-blue" />
          </div>
        </div>
        <p className="mt-6 text-light-muted font-medium">正在生成图片...</p>
        <p className="mt-2 text-sm text-light-muted">这可能需要几秒钟时间</p>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="card h-[400px] flex flex-col items-center justify-center">
        <div className="w-20 h-20 rounded-2xl bg-light-hover flex items-center justify-center mb-4 shadow-inner">
          <ImageIcon className="w-10 h-10 text-light-muted" />
        </div>
        <p className="text-light-muted">点击生成作品查看大图</p>
      </div>
    );
  }

  const getStatusDisplay = (image: GeneratedImage) => {
    if (image.status === 'processing' || image.status === 'pending' || image.status === 'submitting') {
      return (
        <div className="w-full h-full bg-light-hover flex flex-col items-center justify-center">
          <div className="w-10 h-10 border-3 border-light-border border-t-accent-blue rounded-full animate-spin mb-2" />
          <span className="text-xs text-light-muted">生成中</span>
        </div>
      );
    }
    if (image.status === 'failed') {
      return (
        <div className="w-full h-full bg-red-50 flex flex-col items-center justify-center p-2">
          <X className="w-8 h-8 text-red-400 mb-1" />
          <span className="text-xs text-red-400 text-center">生成失败</span>
        </div>
      );
    }
    return null;
  };

  const gridCols = images.length === 1 ? 'grid-cols-1' : 
                   images.length === 2 ? 'grid-cols-2' : 
                   'grid-cols-2';

  const containerHeight = images.length <= 2 ? 'h-[400px]' : 'h-[500px]';

  // 是否显示画布级"上一张 / 下一张"按钮：仅单图 + 父组件传了切换 handler 时启用
  const showNav =
    images.length === 1 && (!!onPrevImage || !!onNextImage);

  return (
    <div className={`card ${containerHeight} overflow-hidden relative`}>
      {/* 画布级位置徽标（仅单图浏览模式） */}
      {showNav && typeof navIndex === 'number' && typeof navTotal === 'number' && (
        <div className="pointer-events-none absolute left-3 top-3 z-20 inline-flex items-center gap-1 rounded-full bg-black/80 px-3 py-1 text-xs font-semibold text-white shadow-lg ring-1 ring-white/30">
          {navIndex + 1} / {navTotal}
        </div>
      )}
      {/* 上一张 */}
      {showNav && (
        <button
          type="button"
          onClick={onPrevImage}
          disabled={!onPrevImage}
          aria-label="上一张"
          className="absolute left-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/80 text-white shadow-xl ring-2 ring-white/40 transition hover:bg-black hover:ring-white/60 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      )}
      {/* 下一张 */}
      {showNav && (
        <button
          type="button"
          onClick={onNextImage}
          disabled={!onNextImage}
          aria-label="下一张"
          className="absolute right-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/80 text-white shadow-xl ring-2 ring-white/40 transition hover:bg-black hover:ring-white/60 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      )}
      <div className={`grid ${gridCols} gap-2 h-full p-2`}>
        {images.map((image, index) => {
          const isCompleted = image.status === 'completed' && !!image.url;
          const isFailed = image.status === 'failed';
          // 仅图生图/局部重绘任务支持恢复其原始参考图。
          const canFillReference =
            !!onFillReference &&
            (image.mode === 'img2img' || image.mode === 'inpaint') &&
            !!image.referenceImages &&
            image.referenceImages.length > 0;

          return (
            <div
              key={image.id}
              className="relative rounded-xl overflow-hidden border border-light-border bg-light-bg/50 flex flex-col"
            >
              {isCompleted ? (
                <>
                  <div
                    className="flex-1 flex items-center justify-center p-2 cursor-pointer overflow-hidden relative"
                    onClick={() => onImageClick?.(image)}
                  >
                    <LazyImage
                      work={{ resultUrl: image.url }}
                      size="medium"
                      alt={image.prompt}
                      className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                    />
                  </div>

                  {modelNameMap?.get(image.model) && (
                    <div className="px-3 pt-1.5 text-[11px] text-slate-400 truncate">
                      {modelNameMap.get(image.model)}
                    </div>
                  )}

                  <div className="flex items-center justify-center gap-2 p-2 bg-light-bg border-t border-light-border flex-wrap">
                    {onFillPrompt && (
                      <button
                        onClick={() => onFillPrompt(image)}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-600 bg-green-50 rounded hover:bg-green-100 transition-colors"
                      >
                        {filledPromptImageId === image.id ? <Check className="w-3 h-3" /> : <Type className="w-3 h-3" />}
                        填充提示词
                      </button>
                    )}
                    {canFillReference && (
                      <button
                        onClick={() => onFillReference!(image)}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-600 bg-purple-50 rounded hover:bg-purple-100 transition-colors"
                      >
                        {filledReferenceImageId === image.id ? <Check className="w-3 h-3" /> : <Images className="w-3 h-3" />}
                        填充参考图
                      </button>
                    )}
                    {onUseAsReference && (
                      <button
                        onClick={() => onUseAsReference(image)}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-accent-blue bg-blue-50 rounded hover:bg-blue-100 transition-colors"
                      >
                        <ImagePlus className="w-3 h-3" />
                        作为参考图
                      </button>
                    )}
                    {onUseAsInpaint && (
                      <button
                        onClick={() => onUseAsInpaint(image)}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-orange-600 bg-orange-50 rounded hover:bg-orange-100 transition-colors"
                      >
                        <Brush className="w-3 h-3" />
                        局部重绘
                      </button>
                    )}
                    {onDownload && (
                      <button
                        onClick={() => onDownload(image)}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-accent-blue rounded hover:bg-blue-600 transition-colors"
                      >
                        <Download className="w-3 h-3" />
                        下载
                      </button>
                    )}
                  </div>

                  {images.length > 1 && (
                    <span className="absolute top-2 left-2 text-xs px-2 py-1 bg-black/50 text-white rounded-lg">
                      {index + 1}
                    </span>
                  )}
                </>
              ) : isFailed ? (
                <>
                  {/* 失败占位图 */}
                  <div className="flex-1 flex items-center justify-center bg-red-50">
                    <div className="flex flex-col items-center justify-center p-4 text-center">
                      <X className="w-10 h-10 text-red-400 mb-2" />
                      <span className="text-sm font-medium text-red-500">生成失败</span>
                      {image.errorMessage && (
                        <span className="mt-1 max-w-[240px] text-xs leading-5 text-red-400/90">
                          {image.errorMessage}
                        </span>
                      )}
                      <span className="text-xs text-red-400/80 mt-0.5">积分已退回</span>
                    </div>
                  </div>
                  {/* 失败任务的可用操作：填充提示词 / 填充参考图 (仅当源有参考图)。
                      不提供"作为参考图"——失败图本身没有结果可用作参考。 */}
                  {(onFillPrompt || canFillReference) && (
                    <div className="flex items-center justify-center gap-2 p-2 bg-light-bg border-t border-light-border flex-wrap">
                      {onFillPrompt && (
                        <button
                          onClick={() => onFillPrompt(image)}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-600 bg-green-50 rounded hover:bg-green-100 transition-colors"
                        >
                          {filledPromptImageId === image.id ? <Check className="w-3 h-3" /> : <Type className="w-3 h-3" />}
                          填充提示词
                        </button>
                      )}
                      {canFillReference && (
                        <button
                          onClick={() => onFillReference!(image)}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-600 bg-purple-50 rounded hover:bg-purple-100 transition-colors"
                        >
                          {filledReferenceImageId === image.id ? <Check className="w-3 h-3" /> : <Images className="w-3 h-3" />}
                          填充参考图
                        </button>
                      )}
                    </div>
                  )}
                  {images.length > 1 && (
                    <span className="absolute top-2 left-2 text-xs px-2 py-1 bg-black/50 text-white rounded-lg">
                      {index + 1}
                    </span>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  {getStatusDisplay(image)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
